/**
 * The undo plumbing the authoring operations share: snapshotting a node,
 * folding steps into one undo entry, and taking steps back when an operation
 * fails part-way.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectData } from '../../scenedata/sceneobjectdata';
import type { UndoCommand } from '../undo/UndoCommand';
import type { CreateChildEntry } from '../undo/CreateObjectCommand';
import { CompositeCommand } from '../undo/CompositeCommand';

/** An object's data with its live pose folded in. */
export function capture(object: SceneObject): SceneObjectData {
    object.captureTransform();
    object.refreshSceneData();
    return object.getSceneObjectData();
}

/** `root`'s descendants as entries a create or delete command records, parents first. */
export function descendantEntries(root: SceneObject): CreateChildEntry[] {
    return root.getDescendants().map(object => ({ object, data: capture(object), parent: object.getParent() ?? root }));
}

/** One undo entry for `steps`: the step itself when there is one, else a composite named `label`. */
export function bundle(label: string, steps: readonly UndoCommand[]): UndoCommand {
    return steps.length === 1 ? steps[0] : new CompositeCommand(label, [...steps]);
}

/**
 * Take back `done`, newest first. Every command is tried even when one fails;
 * failures are thrown together after `cause`, so the reason the operation
 * stopped is not lost.
 */
export async function rollBack(done: readonly UndoCommand[], cause: unknown): Promise<void> {
    const failures: unknown[] = [];
    for (const command of [...done].reverse()) {
        try {
            await command.undo({ silent: true });
        } catch (failure) {
            failures.push(failure);
        }
    }
    if (failures.length > 0) {
        throw new AggregateError([cause, ...failures], 'The operation failed, and taking back what it had done failed too');
    }
}
